import { createContext } from 'react'

import { BrowserService } from '@lib/services/browser'
import {
  type ExperimentService,
  ExperimentsServiceImpl,
  ExperimentClient,
} from '@lib/services/experiments'
import { type PixelExperimentTracker } from '@lib/services/experiments/pixel-tracker'
import { NavigationService } from '@lib/services/navigation/navigation-service'
import type { ServerService } from '@lib/services/server-data'
import { SessionDataServiceImpl, type SessionDataService } from '@lib/services/session-data'
import { UserService } from '@lib/services/user'
import { noopFetch } from '@lib/services/utils/fetch'

import { IS_SERVER } from '@constants/globals'

export interface CommonServices {
  readonly shWebServerService: ServerService
  readonly browserService: BrowserService
  readonly userService: UserService
  readonly experimentService: ExperimentService
  readonly navigationService: NavigationService
  readonly sessionDataService: SessionDataService
}

export const createCommonServices = (
  shWebServerFactory: () => ServerService,
  pixelTracker: PixelExperimentTracker,
  serverQueryParams?: Record<string, string | string[] | undefined>,
): CommonServices => {
  const shWebServerService = shWebServerFactory()
  const browserService = new BrowserService(shWebServerService)
  const userService = new UserService(shWebServerService)
  const navigationService = new NavigationService(serverQueryParams)
  const sessionDataService = new SessionDataServiceImpl(shWebServerService)
  const experimentService = new ExperimentsServiceImpl(
    // on client side, binding fetch to window is needed to avoid error "illegal invocation"
    new ExperimentClient(
      IS_SERVER ? noopFetch : fetch.bind(window),
      shWebServerService,
      pixelTracker,
      navigationService,
    ),
  )

  return {
    shWebServerService,
    browserService,
    userService,
    experimentService,
    navigationService,
    sessionDataService,
  }
}

export const CommonServicesContext = createContext({} as CommonServices)
